// R78 — THE SUPPORT VIEW'S THREE READS, AND THE TWO DIFFERENT 403s THEY CAN BE HANDED.
//
// This is deliberately NOT `loadOwnerData`, and the reason is the whole file: **that transport
// answers `kind: "denied"` for every 403, and this door can be handed two of them that mean opposite
// things.**
//
//   403 `{ ok: false, locked: true, … }`  — `requireSupport`: she IS the owner, the support ticket
//                                           has expired or was cleared. The right act is to re-read
//                                           the door, which puts the passcode form back in front of
//                                           her. The dashboard is not closed.
//   403 `{ ok: false, message: "Not found." }` — `requireOwner`: whoever this is, the whole dashboard
//                                           is closed. `onDenied()` swaps the page.
//
// `OwnerSupportView` already knew this and wrote it down — the comment in `unlock()` says the two
// "are told apart by WHICH refusal came back, not by the status code" — **and it acted on it at
// exactly one of its three call sites.** The two reads that open a member's own readings printed
// "Not found." as a note under a dashboard that should have been replaced (R47: the capability lived
// on the surface that needed it first, and the count is the finding).
//
// **AN UNRECOGNISED 403 IS DENIED, NOT LOCKED**, and that direction is chosen rather than fallen
// into: closing the dashboard on a refusal nobody registered costs her a reload, and the other way
// costs her a screen she may not be allowed to see (the allowlist argument `src/supportView.js` is
// built on, applied to a status code).
//
// `doFetch` is a PARAMETER (R46), so every answer these three routes can give is reachable with no
// browser, no owner session and no database.
import { PASSCODE_UNREAD } from "../supportView.js";

export type SupportAnswer<T> =
  | { ok: true; data: T }
  | { ok: false; kind: "denied" }
  | { ok: false; kind: "locked"; message: string }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

type SupportJson = { ok?: boolean; message?: string; locked?: boolean };

export async function loadSupportData<T>(url: string, doFetch: typeof fetch = fetch): Promise<SupportAnswer<T>> {
  try {
    const res = await doFetch(url, { cache: "no-store" });
    const data = (await res.json().catch(() => null)) as (SupportJson & T) | null;
    if (res.status === 403) {
      return data && data.locked === true
        ? { ok: false, kind: "locked", message: typeof data.message === "string" ? data.message : "" }
        : { ok: false, kind: "denied" };
    }
    if (res.ok && data && data.ok === true) return { ok: true, data: data as T };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// **A 403 SAYS NOTHING** — both of its meanings are ACTS rather than sentences (the dashboard is
// replaced, or the door is re-read and the passcode form comes back). A line under either is a line
// under a screen that is no longer there (R65's ruling). Everything else says something: the
// server's own words where it sent any, the house's where it did not.
export function supportReadNote(answer: SupportAnswer<unknown>, failed: string = PASSCODE_UNREAD): string {
  if (answer.ok || answer.kind === "denied" || answer.kind === "locked") return "";
  if (answer.kind === "refused" && answer.message) return answer.message;
  return failed || PASSCODE_UNREAD;
}

// R88 — **THE SAME NOTE FOR A SURFACE MOUNTED INSIDE THE ALREADY-OPEN DOOR**, where `locked` is a
// SENTENCE rather than an act.
//
// `OwnerSupportView` re-reads the door on a locked answer and the passcode form comes back, so a
// line about it would be a line under a screen that has already changed. `OwnerTranscript` is
// mounted INSIDE that open door and has nothing of its own to re-read, so silence there is a tap
// that does nothing (R25's argument applied to a control rather than to an absent one). It knew
// this and wrote it down; it also hand-typed the three-way expression, and R88 was about to type it
// a second time in the same file (R47: the count is the finding, and this is the count at two).
//
// `denied` still says nothing here, because that one IS still an act: the dashboard is replaced.
export function supportInnerNote(answer: SupportAnswer<unknown>, failed: string): string {
  if (answer.ok || answer.kind === "denied") return "";
  if (answer.kind === "locked") return answer.message || failed;
  return supportReadNote(answer, failed);
}

// R88 — **THE EXPORT WAS A PLAIN `<a href>` AT A ROUTE THAT REFUSES FOUR WAYS IN JSON.**
//
// `/api/owner/support/member/:id/transcript/:conversation/pdf` answers 200 with PDF bytes, and
// otherwise 403 `{ locked: true }` (the support ticket expired), 403 (not the owner at all), 404
// `{ message: "Not found." }` or 500 `{ message: "That could not be exported." }`. An anchor cannot
// read any of that: **the browser NAVIGATES, so a refused export replaces the whole dashboard with a
// raw JSON body**, and takes with it the transcript she just justified opening and the reason she
// typed to open it.
//
// **THE LIKELIEST REFUSAL IS THE WORST ONE, AND THAT IS WHAT MAKES THIS MORE THAN TIDINESS.** The
// support ticket is time-limited and READING a conversation is the slow act, so the refusal she is
// most likely to meet is the locked 403 — arrived at by doing exactly what the feature is for.
//
// It is deliberately NOT `downloadFromUrl` (R56's law): that one throws the body away and raises a
// `window.alert` with one house sentence, so the server's own words — including *"Enter your support
// passcode to open member views."*, the one refusal here she can act on — would never reach her.
// This shares the READ's answer type instead, so one law composes every sentence this door can say
// (R31: the crossing stops existing rather than being watched).
//
// `saveBlobAs` (R56's ONE anchor law) is a PARAMETER along with `doFetch`, so every arm is reachable
// with no browser and no owner session.
export async function saveSupportPdf(
  url: string,
  save: (res: Response) => Promise<boolean>,
  doFetch: typeof fetch = fetch,
): Promise<SupportAnswer<true>> {
  let res: Response;
  try {
    res = await doFetch(url, { cache: "no-store" });
  } catch {
    return { ok: false, kind: "offline" };
  }
  if (res.status === 403) {
    const data = (await res.json().catch(() => null)) as SupportJson | null;
    return data && data.locked === true
      ? { ok: false, kind: "locked", message: typeof data.message === "string" ? data.message : "" }
      : { ok: false, kind: "denied" };
  }
  // **THE BYTES ARE THE ANSWER, AND WRITING THEM IS PART OF IT** (R53: a law's answer is only worth
  // what the caller does with it). A 200 whose blob never reaches her disk is a refusal, not a
  // success with a missing step — which is exactly the distinction `saveBlobAs` already returns and
  // `downloadFromUrl` already honours.
  if (res.ok) {
    return (await save(res)) ? { ok: true, data: true } : { ok: false, kind: "refused", message: "" };
  }
  const data = (await res.json().catch(() => null)) as SupportJson | null;
  return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
}
